import type { CalibrationProposal } from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";

/** Calibration proposals (admin DB). Not org-scoped. */
export const useCalibrationProposals = (status = "proposed") =>
	useQuery({
		queryFn: async () => {
			const response = await apiClient.get<CalibrationProposal[]>(
				ENDPOINTS.shadow.plausibility.calibrationProposals,
				{ params: { status } },
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.calibrationProposals(status),
	});

export const useReviewProposal = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: {
			action: "accept" | "reject";
			proposalId: string;
		}) => {
			const response = await apiClient.post<CalibrationProposal>(
				ENDPOINTS.shadow.plausibility.calibrationReview(
					input.proposalId,
					input.action,
				),
			);
			return response.data;
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["shadow", "plausibility", "calibration"],
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.plausibility.globalPriors,
				}),
			]);
		},
	});
};

export interface CalibrationRunResult {
	proposals: number;
	samples: number;
	tenants: number;
}

export const useRunCalibration = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const response = await apiClient.post<CalibrationRunResult>(
				ENDPOINTS.shadow.plausibility.calibrationRun,
			);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["shadow", "plausibility", "calibration"],
			});
		},
	});
};
